import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Star, Instagram, Youtube, Facebook } from "lucide-react";
import { Logo } from "./Logo";
import { useSettings } from "../context/SettingsContext";
import { useModal } from "../context/ModalContext";
import { WhatsAppButton } from "./WhatsAppButton";
import { BUSINESS, GOOGLE_BUSINESS_URL, locationPath } from "../lib/useSeo";

const AREAS = ["Northolt", "Greenford", "Southall", "South Harrow"];
const SERVICES = [
  { label: "Automatic Lessons", path: "/automatic-driving-lessons-northolt" },
  { label: "Manual Lessons", path: "/manual-driving-lessons-northolt" },
  { label: "Refresher Lessons" },
  { label: "Pass Plus" },
  { label: "Mock Test" },
];
const SOCIALS = [
  { icon: Instagram, href: "https://www.instagram.com/suk._.driving/", label: "Instagram" },
  { icon: Youtube, href: "https://www.youtube.com/@sukdrivingschool7462", label: "YouTube" },
  { icon: Facebook, href: "https://www.facebook.com/sukdrivingschool/", label: "Facebook" },
];

export function Footer() {
  const { settings } = useSettings();
  const { open } = useModal();
  const reviewsUrl = GOOGLE_BUSINESS_URL;

  return (
    <footer className="relative bg-brand-ink text-neutral-300 overflow-hidden grain" data-testid="site-footer">
      <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full brand-gradient opacity-20 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10">
          {/* About */}
          <div className="lg:col-span-4">
            <Logo size={38} dark />
            <p className="mt-5 text-sm leading-relaxed text-neutral-400 max-w-xs">
              We provide manual and automatic driving lessons across Northolt, Greenford, Southall and South Harrow.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <a href={reviewsUrl} target="_blank" rel="noopener noreferrer" className="btn-outline !bg-white/5 !border-white/15 !text-white !py-2.5 text-sm inline-flex" data-testid="google-reviews-btn">
                <Star size={16} className="fill-brand-amber text-brand-amber" /> Google Reviews
              </a>
              <WhatsAppButton label="WhatsApp Us" className="!py-2.5 text-sm" testId="footer-whatsapp-btn" size={16} />
            </div>
            <div className="flex gap-2.5 mt-6">
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-orange transition-colors" data-testid={`social-${s.label.toLowerCase()}`}>
                  <s.icon size={17} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-heading font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/" className="hover:text-brand-amber">Home</Link></li>
              <li><button onClick={() => open("quote")} className="hover:text-brand-amber" data-testid="footer-quote">Get a Quote</button></li>
              <li><button onClick={() => open("booking")} className="hover:text-brand-amber">Book a Lesson</button></li>
              <li><button onClick={() => open("callback")} className="hover:text-brand-amber">Request Callback</button></li>
              <li><Link to="/learn" className="hover:text-brand-amber">Learning Guides</Link></li>
              <li><Link to="/blog" className="hover:text-brand-amber">Driving Blog</Link></li>
              <li><Link to="/contact" className="hover:text-brand-amber">Contact</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <h4 className="text-white font-heading font-semibold mb-4">Services & Areas</h4>
            <ul className="grid grid-cols-1 gap-2.5 text-sm">
              {SERVICES.slice(0, 4).map((service) => (
                <li key={service.label}>
                  {service.path
                    ? <Link to={service.path} className="hover:text-brand-amber text-left">{service.label}</Link>
                    : <button onClick={() => open("booking")} className="hover:text-brand-amber text-left">{service.label}</button>}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-xs text-neutral-400">
              {AREAS.map((a) => (
                <Link key={a} to={locationPath(a.toLowerCase().replace(/\s+/g, "-"))} className="hover:text-brand-amber">{a}</Link>
              ))}
            </div>
          </div>

          {/* Contact + Hours (always visible on mobile) */}
          <div className="lg:col-span-3">
            <h4 className="text-white font-heading font-semibold mb-4">Get in Touch</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-3"><MapPin size={17} className="text-brand-orange shrink-0 mt-0.5" /><span>{settings.address || BUSINESS.address}</span></li>
              <li><a href={`tel:${settings.phone_link || ""}`} className="flex gap-3 hover:text-brand-amber" data-testid="footer-phone"><Phone size={17} className="text-brand-orange shrink-0" />{settings.phone || "07956 120 510"}</a></li>
              <li><a href={`mailto:${settings.email || ""}`} className="flex gap-3 hover:text-brand-amber break-all"><Mail size={17} className="text-brand-orange shrink-0" />{settings.email}</a></li>
              <li className="flex gap-3"><Clock size={17} className="text-brand-orange shrink-0 mt-0.5" /><span>Mon–Fri: 10:00 – 14:00<br/>Sat &amp; Sun: Closed</span></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-neutral-500">
          <p>© {new Date().getFullYear()} S-UK Driving School. All rights reserved.</p>
          <p>DVSA Approved · Manual & Automatic · Fully Insured</p>
        </div>
      </div>
    </footer>
  );
}
